import {
  Form,
  Button,
  Dropdown,
  Modal,
  Row,
  Col,
  Badge,
} from "react-bootstrap";
import { useState, useEffect } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
  EditIcon,
  FileListIcon,
  FilterTwoIcon,
  TrashIcon,
  ExportIcon,
  AddCircleIcon,
} from "../Icons";
import TableComponent from "../TableComponent";
import DotsHorizontalIcon from "mdi-react/DotsHorizontalIcon";
import EyeIcon from "mdi-react/EyeIcon";
import CachedIcon from "mdi-react/CachedIcon";
import {
  FaUser,
  FaIdCard,
  FaPhone,
  FaCalendar,
  FaShieldAlt,
} from "react-icons/fa";
import ConfirmDialog from "../ConfirmDialogue";
import { useAuth } from "../../hooks/useAuth";
import queryString from "query-string";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import useDebounce, { useQueryParams } from "../../utils/hooks";
import ModalLoader from "../utils/ModalLoader";
import moment from "moment";
import DriverModal from "./AddDriver";
import DriverDetailsModal from "./DriverDetailModal";
import "../../assets/scss/fleet/style.scss";

// Validation schema for driver forms
const driverValidationSchema = Yup.object().shape({
  employee_id: Yup.string().required("Employee ID is required"),
  licenseNumber: Yup.string().required("License Number is required"),
  licenseClass: Yup.string().required("License Class is required"),
  licenseExpiry: Yup.date().required("License Expiry is required"),
  contactPhone: Yup.string().required("Contact phone is required"),
  medicalCertExpiry: Yup.date(),
  emergencyContactName: Yup.string(),
  emergencyContactPhone: Yup.string(),
  emergencyContactRelation: Yup.string(),
  status: Yup.string().required("Status is required"),
  safetyScore: Yup.number()
    .min(0)
    .max(100),
  notes: Yup.string(),
});

// Filter validation schema
const filterValidationSchema = Yup.object().shape({
  q: Yup.string(),
  status: Yup.string(),
  licenseClass: Yup.string(),
});

export default function FleetDrivers() {
  const { backendUrl } = useAuth();
  const queryClient = useQueryClient();
  const [showFilter, setShowFilter] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isMainLoading, setIsMainLoading] = useState(false);
  const [editDriver, setEditDriver] = useState(null);

  const initialFilterParams = {
    q: "",
    status: "",
    licenseClass: "",
  };

  const initialDriverValues = {
    employee_id: "",
    licenseNumber: "",
    licenseClass: "C",
    licenseExpiry: "",
    contactPhone: "",
    medicalCertExpiry: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    emergencyContactRelation: "",
    status: "active",
    safetyScore: 0,
    notes: "",
  };

  const [queryParams, setQueryParams] = useQueryParams({
    page: 1,
    limit: 20,
    ...initialFilterParams,
  });

  const [filterParams, setFilterParams] = useState({
    ...initialFilterParams,
    ...queryParams,
  });

  const debouncedFilterParams = useDebounce(filterParams, 500);

  useEffect(() => {
    setQueryParams({ ...queryParams, ...debouncedFilterParams });
  }, [debouncedFilterParams]);

  const statusOptions = [
    { value: "active", label: "Active" },
    { value: "inactive", label: "Inactive" },
    { value: "suspended", label: "Suspended" },
    { value: "terminated", label: "Terminated" },
  ];

  const licenseClassOptions = ["A", "B", "C", "D", "E", "CE"];

  // Fetch drivers
  const fetchDrivers = async (params) => {
    const response = await fetch(
      `${backendUrl}/api/fleet/drivers?${queryString.stringify(params)}`,
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        credentials: "include",
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message);
    }

    const result = await response.json();
    return result.data;
  };

  const {
    data = { drivers: [], count: 0, page: 1, limit: 20 },
    refetch,
    isLoading,
  } = useQuery(
    ["FLEET_DRIVERS", queryParams],
    () => fetchDrivers(queryParams),
    {
      keepPreviousData: true,
      staleTime: 0,
    }
  );

  // Fetch driver details
  const fetchDriverDetails = async (driverId) => {
    try {
      setIsMainLoading(true);
      const response = await fetch(
        `${backendUrl}/api/fleet/drivers/${driverId}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      const result = await response.json();
      return result.data;
    } catch (error) {
      console.log(error);
    } finally {
      setIsMainLoading(false);
    }
  };

  const { data: driverDetails, refetch: refetchDetails } = useQuery(
    ["DRIVER_DETAILS", selectedDriver?.driver_id],
    () => fetchDriverDetails(selectedDriver.driver_id),
    {
      enabled: !!selectedDriver && showDetailsModal,
      staleTime: 0,
    }
  );

  // Create driver mutation
  const createDriverMutation = useMutation(
    async (driverData) => {
      const response = await fetch(`${backendUrl}/api/fleet/drivers`, {
        method: "POST",
        headers: {
          // Accept: "application/json",
        },
        credentials: "include",
        body: driverData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Driver created successfully");
        queryClient.invalidateQueries(["FLEET_DRIVERS"]);
        setShowAddModal(false);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  // Update driver mutation
  const updateDriverMutation = useMutation(
    async (driverData) => {
      console.log(data);
      const response = await fetch(
        `${backendUrl}/api/fleet/driver/${editDriver.driver_id}`,
        {
          method: "PATCH",
          credentials: "include",
          body: driverData,
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Driver updated successfully");
        queryClient.invalidateQueries(["FLEET_DRIVERS"]);
        setShowEditModal(false);
        setEditDriver(null);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  // Delete driver mutation
  const deleteDriverMutation = useMutation(
    async (driverId) => {
      const response = await fetch(
        `${backendUrl}/api/fleet/drivers/${driverId}`,
        {
          method: "DELETE",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message);
      }

      return response.json();
    },
    {
      onSuccess: () => {
        toast.success("Driver deleted successfully");
        queryClient.invalidateQueries(["FLEET_DRIVERS"]);
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilterParams({
      ...filterParams,
      [name]: value,
    });
  };

  const resetFilters = () => {
    setFilterParams(initialFilterParams);
    setQueryParams({
      ...queryParams,
      ...initialFilterParams,
    });
  };

  const handleSearch = () => {
    setQueryParams({
      ...queryParams,
      ...filterParams,
    });
  };

  const handleViewDetails = (driver) => {
    setSelectedDriver(driver);
    setShowDetailsModal(true);
  };

  const handleEdit = (driver) => {
    console.log("editing", driver);
    setEditDriver(driver);
    setShowEditModal(true);
  };

  const handleDelete = async (driver) => {
    const confirmed = await ConfirmDialog({
      title: "Delete Driver",
      description: `Are you sure you want to delete <b>${
        driver.licenseNumber
      }</b>?`,
    });

    if (confirmed) {
      deleteDriverMutation.mutate(driver.driver_id);
    }
  };

  const handleCreateDriver = async (values, { setSubmitting, resetForm }) => {
    const confirmed = await ConfirmDialog({
      title: "Create a new Driver",
      description: `Are you sure you want to create this driver?`,
    });

    if (confirmed) {
      // Convert values to FormData if needed
      const formData = new FormData();
      Object.keys(values).forEach((key) => {
        formData.append(key, values[key]);
      });

      createDriverMutation.mutate(formData, {
        onSettled: () => {
          setSubmitting(false);
          resetForm();
        },
      });
    } else {
      setSubmitting(false);
    }
  };

  const handleUpdateDriver = async (values, { setSubmitting }) => {
    const confirmed = await ConfirmDialog({
      title: "Update Driver",
      description: `Are you sure you want to update this driver?`,
    });

    if (confirmed) {
      updateDriverMutation.mutate(
        {
          driverId: editDriver.driver_id,
          driverData: values,
        },
        {
          onSettled: () => {
            setSubmitting(false);
          },
        }
      );
    } else {
      setSubmitting(false);
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      active: "view_status_all",
      inactive: "view_status_pending",
      suspended: "view_status_reject",
      terminated: "view_status_partly",
    };
    return statusMap[status] || "";
  };

  const tableHead = () => (
    <thead>
      <tr>
        <th />
        <th>Employee ID</th>
        <th>License Number</th>
        <th>License Class</th>
        <th>License Expiry</th>
        <th>Medical Cert Expiry</th>
        <th>Safety Score</th>
        <th>Status</th>
      </tr>
    </thead>
  );

  const displayTableData = (driver) => (
    <>
      <td>
        <Dropdown>
          <Dropdown.Toggle
            variant=""
            className="bg-white border-0"
            bsPrefix="print more"
            style={{ height: "3px" }}
          >
            <DotsHorizontalIcon size={18} />
          </Dropdown.Toggle>
          <Dropdown.Menu
            popperConfig={{ strategy: "fixed" }}
            renderOnMount
            className="dropdown-with-icons"
          >
            <Dropdown.Item onClick={() => handleViewDetails(driver)}>
              <EyeIcon size={16} className="me-2" />
              View Details
            </Dropdown.Item>
            <Dropdown.Item onClick={() => handleEdit(driver)}>
              <EditIcon size={16} className="me-2" />
              Edit Driver
            </Dropdown.Item>
            {/* <Dropdown.Item>
              <FileListIcon color={"black"} size={16} className="me-2" />
              Documents
            </Dropdown.Item> */}
            {/* <Dropdown.Divider />
            <Dropdown.Item
              className="text-danger"
              onClick={() => handleDelete(driver)}
            >
              <TrashIcon size={16} className="me-2" />
              Delete
            </Dropdown.Item> */}
          </Dropdown.Menu>
        </Dropdown>
      </td>
      <td className="fw-semibold">{driver.employee_id}</td>
      <td className="fw-semibold">{driver.licenseNumber}</td>
      <td>{driver.licenseClass}</td>
      <td>
        {driver.licenseExpiry
          ? moment(driver.licenseExpiry).format("MMM DD, YYYY")
          : "N/A"}
      </td>
      <td>
        {driver.medicalCertExpiry
          ? moment(driver.medicalCertExpiry).format("MMM DD, YYYY")
          : "N/A"}
      </td>
      <td>
        <span
          className={`badge ${
            driver.safetyScore >= 90
              ? "bg-success"
              : driver.safetyScore >= 70
              ? "bg-warning"
              : "bg-danger"
          }`}
        >
          {driver.safetyScore || 0}
        </span>
      </td>
      <td>
        <div className={`px-2 ${getStatusClass(driver.status)}`}>
          {driver.status?.toUpperCase()}
        </div>
      </td>
    </>
  );

  return (
    <section className="invoice-wrapped fleet driver">
      <main className="cash-book">
        <div className="content">
          <div className="content-main">
            {/* Filter Sidebar with Formik */}
            <div className={`filter-area ${showFilter ? "open" : "close"}`}>
              <div className="filter-header">
                <h2>
                  <FilterTwoIcon size={20} />
                  Filters
                </h2>
                <Button
                  variant="white"
                  onClick={() => setShowFilter(!showFilter)}
                >
                  ×
                </Button>
              </div>
              <div className="filter-body">
                <Formik
                  initialValues={filterParams}
                  validationSchema={filterValidationSchema}
                  onSubmit={handleSearch}
                  enableReinitialize
                >
                  {({ values, handleChange, handleSubmit, resetForm }) => (
                    <Form onSubmit={handleSubmit}>
                      <div className="body">
                        <Form.Group className="mb-2-5">
                          <Form.Label>Search</Form.Label>
                          <Form.Control
                            name="q"
                            placeholder="License Number, Employee ID"
                            value={values.q}
                            onChange={(e) => {
                              handleChange(e);
                              handleFilterChange(e);
                            }}
                          />
                        </Form.Group>

                        <Form.Group className="mb-2-5">
                          <Form.Label>Status</Form.Label>
                          <Form.Select
                            name="status"
                            value={values.status}
                            onChange={(e) => {
                              handleChange(e);
                              handleFilterChange(e);
                            }}
                          >
                            <option value="">All Statuses</option>
                            {statusOptions.map((opt) => (
                              <option key={opt.value} value={opt.label}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>

                        <Form.Group className="mb-2-5">
                          <Form.Label>License Class</Form.Label>
                          <Form.Select
                            name="licenseClass"
                            value={values.licenseClass}
                            onChange={(e) => {
                              handleChange(e);
                              handleFilterChange(e);
                            }}
                          >
                            <option value="">All Classes</option>
                            {licenseClassOptions.map((cls) => (
                              <option key={cls} value={cls}>
                                Class {cls}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </div>

                      <div className="buttons rounded">
                        <Button
                          onClick={() => {
                            resetForm();
                            resetFilters();
                          }}
                          variant="white"
                          type="button"
                        >
                          Reset
                        </Button>
                        <Button type="submit" variant="primary">
                          Search
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>

            {/* Main Content */}
            <div className="content-body">
              <header className="text-nowrap">
                <h1>
                  {!showFilter && (
                    <button
                      onClick={() => setShowFilter(!showFilter)}
                      className="btn filter"
                    >
                      <FilterTwoIcon size={20} />
                    </button>
                  )}
                  Fleet Drivers
                  <button
                    title="Refresh"
                    onClick={() => refetch()}
                    className="btn text-primary mx-3"
                  >
                    <CachedIcon />
                  </button>
                </h1>
                <div className="actions d-flex align-items-baseline gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => setShowAddModal(true)}
                  >
                    <AddCircleIcon className="me-1" />
                    Add Driver
                  </Button>
                  <div className="btn bg print">
                    Export
                    <ExportIcon className="ms-2" />
                  </div>
                </div>
              </header>

              <div className="px-md-4">
                <TableComponent
                  responsive
                  borderless
                  striped
                  className="product-table text-nowrap"
                  tableHeadsFunction={tableHead}
                  tableDataRowFunction={displayTableData}
                  mainDataArray={data.drivers}
                />
              </div>

              {data.drivers.length === 0 && (
                <div className="d-flex justify-content-center">
                  <div className="info text-center">
                    <h2 className="my-2">No Drivers Available</h2>
                  </div>
                </div>
              )}

              {/* Pagination */}
              <div className="d-flex justify-content-between px-3 align-items-center pagination">
                <div className="pagination_left">
                  <p className="m-0 p-0">Show</p>
                  <select
                    value={queryParams.limit}
                    name="limit"
                    className="form-select"
                    onChange={(e) =>
                      setQueryParams({
                        ...queryParams,
                        limit: e.target.value,
                        page: 1,
                      })
                    }
                  >
                    <option value="10">10 rows</option>
                    <option value="20">20 rows</option>
                    <option value="30">30 rows</option>
                    <option value="40">40 rows</option>
                    <option value="50">50 rows</option>
                    <option value="100">100 rows</option>
                  </select>
                </div>
                <p className="m-0">
                  Page {data.page} of {Math.ceil(data.count / data.limit)} (
                  {data.count} total)
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Add Driver Modal with Formik */}
        {showAddModal && (
          <Formik
            initialValues={initialDriverValues}
            validationSchema={driverValidationSchema}
            onSubmit={handleCreateDriver}
          >
            {(formikProps) => (
              <DriverModal
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                formikProps={formikProps}
                mutation={createDriverMutation}
                licenseClassOptions={licenseClassOptions}
                statusOptions={statusOptions}
                isEditMode={false}
              />
            )}
          </Formik>
        )}

        {/* Edit Driver Modal with Formik */}
        {showEditModal && editDriver && (
          <Formik
            initialValues={editDriver}
            validationSchema={driverValidationSchema}
            onSubmit={handleUpdateDriver}
            enableReinitialize
          >
            {(formikProps) => (
              <DriverModal
                showModal={showEditModal}
                setShowModal={setShowEditModal}
                formikProps={formikProps}
                mutation={updateDriverMutation}
                licenseClassOptions={licenseClassOptions}
                statusOptions={statusOptions}
                isEditMode={true}
                backendUrl={backendUrl}
              />
            )}
          </Formik>
        )}
      </main>

      {showDetailsModal && (
        <DriverDetailsModal
          driver={driverDetails}
          showModal={showDetailsModal}
          setShowModal={setShowDetailsModal}
          refetch={refetchDetails}
        />
      )}
      <ModalLoader show={isLoading || isMainLoading} />
    </section>
  );
}
